import { Avatar, Button, ConfirmSheet, Icons } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { customerBackendApi } from "../../features/api/backend.ts";
import { useT } from "../../features/i18n/index.ts";
import { LocaleSwitcher } from "../../features/i18n/locale-switcher.tsx";
import { useLiff } from "../../features/liff/use-liff.ts";
import { useSession } from "../../features/session/session-context.tsx";
import { getMode } from "../../features/tonight/mode.ts";

export const Route = createFileRoute("/_tabs/me")({
  component: MeScreen,
});

function MeScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { profile } = useLiff();
  const {
    mode,
    tableCode,
    settings,
    setAllowGifts,
    setAllowContact,
    blockedTables,
    reports,
    sentSocial,
    order,
    leaveVenue,
    toast,
  } = useSession();
  const modeMeta = getMode(mode);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pendingOutgoing = sentSocial.filter((request) =>
    ["pending_acceptance", "accepted"].includes(request.status),
  );

  const confirmLeave = () => {
    const result = leaveVenue();
    setLeaveOpen(false);
    if (!result.ok) {
      toast(result.reason ?? t("me.leaveBlockedFallback"));
      return;
    }
    navigate({ to: "/welcome" });
  };

  // PRIV-032 / FR-CUS-132: self-serve data-deletion request. Strips PII
  // fields server-side and audits the request; the current venue night keeps
  // working (check-in/order state is untouched — see routes/profiles.rs).
  const confirmDeleteMyData = async () => {
    setDeleteOpen(false);
    if (!customerBackendApi) {
      toast(t("me.deleteBackendOnly"));
      return;
    }
    setDeleting(true);
    try {
      await customerBackendApi.deleteMyProfile();
      toast(t("me.deletedToast"));
    } catch {
      toast(t("me.deleteFailedToast"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="jod-kicker">{t("common.profileTonight")}</div>
        <h1 className="h-display text-[1.5rem]">{t("nav.me")}</h1>
      </div>

      <button
        type="button"
        onClick={() => navigate({ to: "/profile" })}
        className="glass-hi relative flex w-full items-center gap-4 overflow-hidden rounded-xl p-4 text-left"
      >
        <div className="jod-sheen" aria-hidden="true" />
        <Avatar
          imageUrl={profile?.pictureUrl}
          label={profile?.displayName ?? t("common.you")}
          glyph={profile?.pictureUrl ? undefined : "🌙"}
          size={56}
          ring
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display font-semibold text-cream">
            {profile?.displayName ?? t("common.you")}
          </p>
          <p className="text-cream-3 text-sm">
            {t("tonightMode.tableAndMode", { table: tableCode, mode: modeMeta.label })}
          </p>
          {order ? (
            <p className="text-gold text-xs">{t("me.lastOrder", { status: order.status })}</p>
          ) : null}
        </div>
        <span className="shrink-0 text-cream-3">
          <Icons.IconChevDown size={18} style={{ transform: "rotate(-90deg)" }} />
        </span>
      </button>

      <Button variant="ghost" className="w-full" onClick={() => navigate({ to: "/profile" })}>
        <Icons.IconMe size={16} /> {t("me.editProfile")}
      </Button>

      <Button variant="ghost" className="w-full" onClick={() => navigate({ to: "/tonight-mode" })}>
        <Icons.IconEye size={16} /> {t("me.adjustMode")}
      </Button>

      <div className="space-y-2.5">
        <ToggleRow
          icon={<Icons.IconGiftNav size={18} />}
          label={t("me.allowGifts")}
          sub={t("me.allowGiftsSub")}
          on={settings.allowGifts}
          onToggle={() => {
            setAllowGifts(!settings.allowGifts);
            toast(settings.allowGifts ? t("me.giftsOffToast") : t("me.giftsOnToast"));
          }}
        />
        <ToggleRow
          icon={<Icons.IconChat size={18} />}
          label={t("me.allowContact")}
          sub={t("me.allowContactSub")}
          on={settings.allowContact}
          onToggle={() => {
            setAllowContact(!settings.allowContact);
            toast(settings.allowContact ? t("me.contactOffToast") : t("me.contactOnToast"));
          }}
        />
      </div>

      <div className="glass rounded-xl p-4">
        <div className="jod-kicker mb-3">{t("me.safetyKicker")}</div>
        <div className="space-y-2.5">
          <button
            type="button"
            onClick={() => navigate({ to: "/blocked" })}
            className="flex w-full items-center gap-3 rounded-lg bg-[oklch(100%_0_0_/_3%)] px-4 py-3.5 text-left"
          >
            <span className="text-cream-2">
              <Icons.IconShield size={18} />
            </span>
            <span className="flex-1 text-cream text-sm">{t("blocked.title")}</span>
            <span className="max-w-[9rem] truncate text-cream-3 text-xs">
              {blockedTables.length > 0 ? blockedTables.join(", ") : t("me.none")}
            </span>
            <Icons.IconChevDown size={16} style={{ transform: "rotate(-90deg)" }} />
          </button>
          <LinkRow
            icon={<Icons.IconFlag size={18} />}
            label={t("me.reports")}
            value={
              reports.length > 0
                ? t("common.itemsCount", { count: reports.length })
                : t("me.noneYet")
            }
          />
          <button
            type="button"
            onClick={() => navigate({ to: "/requests" })}
            className="flex w-full items-center gap-3 rounded-lg bg-[oklch(100%_0_0_/_3%)] px-4 py-3.5 text-left"
          >
            <span className="text-cream-2">
              <Icons.IconBell size={18} />
            </span>
            <span className="flex-1 text-cream text-sm">{t("me.pendingConsent")}</span>
            <span className="text-cream-3 text-xs">
              {t("me.sentOut", { count: pendingOutgoing.length })}
            </span>
            <Icons.IconChevDown size={16} style={{ transform: "rotate(-90deg)" }} />
          </button>
        </div>
      </div>

      <div className="glass flex w-full items-center gap-3 rounded-lg px-4 py-3.5">
        <span className="text-cream-2">
          <Icons.IconChat size={18} />
        </span>
        <span className="flex-1 text-cream text-sm">{t("settings.language")}</span>
        <LocaleSwitcher />
      </div>

      <Button variant="destructive" className="mt-2 w-full" onClick={() => setLeaveOpen(true)}>
        {t("me.leave")}
      </Button>

      <Button
        variant="ghost"
        className="w-full text-red-300"
        disabled={deleting}
        onClick={() => setDeleteOpen(true)}
      >
        <Icons.IconShield size={16} /> {t("me.deleteMyData")}
      </Button>

      <ConfirmSheet
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        title={t("me.leaveConfirmTitle")}
        description={
          order && !["served", "cancelled", "rejected"].includes(order.status)
            ? t("me.leaveConfirmBusy")
            : t("me.leaveConfirmBody")
        }
        confirmLabel={t("me.leaveConfirm")}
        cancelLabel={t("common.notYet")}
        onConfirm={confirmLeave}
      />

      <ConfirmSheet
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        destructive
        title={t("me.deleteConfirmTitle")}
        description={t("me.deleteConfirmBody")}
        confirmLabel={t("me.deleteConfirm")}
        cancelLabel={t("common.notYet")}
        onConfirm={() => {
          void confirmDeleteMyData();
        }}
      />
    </div>
  );
}

interface ToggleRowProps {
  icon: ReactNode;
  label: string;
  sub: string;
  on: boolean;
  onToggle: () => void;
}

function ToggleRow({ icon, label, sub, on, onToggle }: ToggleRowProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      className="glass flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left"
    >
      <span className="text-cream-2">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-cream text-sm">{label}</div>
        <div className="text-cream-3 text-xs">{sub}</div>
      </div>
      <span
        className={`flex h-6 w-10 shrink-0 items-center rounded-full p-0.5 transition-colors ${
          on ? "bg-lime" : "bg-[oklch(100%_0_0_/_12%)]"
        }`}
      >
        <span
          className="size-5 rounded-full bg-cream transition-transform"
          style={{ transform: on ? "translateX(1rem)" : "translateX(0)" }}
        />
      </span>
    </button>
  );
}

interface LinkRowProps {
  icon: ReactNode;
  label: string;
  value?: string;
}

function LinkRow({ icon, label, value }: LinkRowProps) {
  return (
    <div className="flex w-full items-center gap-3 rounded-lg bg-[oklch(100%_0_0_/_3%)] px-4 py-3.5">
      <span className="text-cream-2">{icon}</span>
      <span className="flex-1 text-cream text-sm">{label}</span>
      {value ? <span className="max-w-[9rem] truncate text-cream-3 text-xs">{value}</span> : null}
      <Icons.IconChevDown size={16} style={{ transform: "rotate(-90deg)" }} />
    </div>
  );
}
